// Persist middleware — debounced snapshot save on every game-state change.
// The repo is injected so the store doesn't know whether it's saving to
// memory, IndexedDB, or (later) the cloud.

import { snapshot } from '@biz-kid/engine';
import type { Repo, SaveKey } from '@biz-kid/persistence';
import type { GameStoreInstance } from './store.js';

export type AttachPersistenceArgs = {
  store: GameStoreInstance;
  repo: Repo;
  key: SaveKey;
  /** Coalesce rapid changes into a single save. Default 750ms. */
  debounceMs?: number;
  /** Override for tests — defaults to () => new Date().toISOString(). */
  now?: () => string;
};

/** Returns an unsubscribe function — call on app teardown to stop saving. */
export function attachPersistence(args: AttachPersistenceArgs): () => void {
  const { store, repo, key } = args;
  const debounceMs = args.debounceMs ?? 750;
  const now = args.now ?? (() => new Date().toISOString());

  let timer: ReturnType<typeof setTimeout> | undefined;
  let lastSavedRef = store.getState().game;

  const unsubscribe = store.subscribe((state) => {
    if (state.game === lastSavedRef) return;
    if (timer !== undefined) clearTimeout(timer);
    timer = setTimeout(() => {
      const snap = snapshot(state.game, now());
      lastSavedRef = state.game;
      void repo.putSnapshot(key, snap);
    }, debounceMs);
  });

  return () => {
    unsubscribe();
    if (timer !== undefined) clearTimeout(timer);
  };
}

/**
 * Boot helper — restore from repo if there's a saved snapshot, otherwise
 * boot fresh with the given store. Returns whether a snapshot was loaded.
 */
export async function loadOrInit(repo: Repo, key: SaveKey): Promise<ReturnType<Repo['getSnapshot']>> {
  return repo.getSnapshot(key);
}
